import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import authAxios from '../utils/authAxios';
import { Input, Select, Button, DangerButton } from '../styles/TableStyles';
import TypeaheadSelect from './TypeaheadSelect';
import {
  DrawerBackdrop,
  Drawer,
  DrawerHeader,
  DrawerTitle,
  SectionCard,
  SectionTitle,
  Field,
  FieldLabel,
  FieldValue,
  FieldGrid,
  ActionRow,
  InlineTable,
  NegativeValue,
  InlineAddRow,
  EmptyHint,
} from '../styles/ChargeByStyleStyles';

const READONLY_STYLE = { backgroundColor: 'var(--grey-100)', cursor: 'not-allowed' };

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '$0.00';
};

const fmtBagWeight = (v) => {
  if (v == null) return '';
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toString() : '';
};

const formStateFromItem = (item) => ({
  commodityUniqueId: String(item?.CommodityUniqueID ?? ''),
  bagType:           item?.BagType ?? '',
  productType:       item?.ProductType ?? 'Bag',
  isTWB:             item?.IsTWB === true || item?.IsTWB === 1,
  count:             item?.Count == null ? '' : String(item.Count),
  bagWeight:         item?.BagWeight == null ? '' : parseFloat(item.BagWeight).toString(),
  bulkWeight:        item?.BulkWeight == null ? '' : parseFloat(item.BulkWeight).toString(),
  tihi:              item?.TiHi ?? '',
});

export default function ChargeByStyleDetailPanel({ item, onClose, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => formStateFromItem(item));

  const [commodities, setCommodities] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [bagCosts, setBagCosts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [boxDiffs, setBoxDiffs] = useState([]);
  const [newMethodId, setNewMethodId] = useState('');
  const [busy, setBusy] = useState(false);

  // Re-sync the form whenever the parent passes a new item snapshot (e.g. after save).
  useEffect(() => {
    setForm(formStateFromItem(item));
  }, [item]);

  // Reference data loads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [comm, pts, bcs, meths] = await Promise.all([
          authAxios.get('/api/commodities/dropdown'),
          authAxios.get('/api/packtypes'),
          authAxios.get('/api/bagcosts'),
          authAxios.get('/api/methods'),
        ]);
        if (cancelled) return;
        setCommodities(Array.isArray(comm.data) ? comm.data : []);
        setPackTypes(Array.isArray(pts.data) ? pts.data : []);
        setBagCosts(Array.isArray(bcs.data) ? bcs.data : []);
        setMethods(Array.isArray(meths.data) ? meths.data : []);
      } catch (err) {
        console.error('Failed to load Charge-by-Style reference data:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // BoxDifference fetch — refetched after add/delete.
  const fetchBoxDiffs = useCallback(async () => {
    try {
      const res = await authAxios.get('/api/boxdifference', { params: { chargeByStyleId: item.UniqueID } });
      setBoxDiffs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch BoxDifference rows:', err);
    }
  }, [item.UniqueID]);

  useEffect(() => { fetchBoxDiffs(); }, [fetchBoxDiffs]);

  // Esc closes the drawer.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const commodityOptions = useMemo(
    () => commodities.map(c => ({ value: c.UniqueID, label: c.Commodity })),
    [commodities]
  );
  const groups = useMemo(
    () => [...new Set(packTypes.map(p => p.Type).filter(Boolean))].sort(),
    [packTypes]
  );
  const styleNames = useMemo(
    () => [...new Set(packTypes.map(p => p.Style).filter(Boolean))].sort(),
    [packTypes]
  );
  const packByStyle = useMemo(() => {
    const m = new Map();
    packTypes.forEach(p => { if (p.Style) m.set(p.Style, p); });
    return m;
  }, [packTypes]);
  const bagCostByStyle = useMemo(() => {
    const m = new Map();
    bagCosts.forEach(b => { if (b.BagName) m.set(b.BagName, b); });
    return m;
  }, [bagCosts]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'productType') {
        if (value !== 'Bag') { next.count = ''; next.bagWeight = ''; }
        else { next.bulkWeight = ''; }
      }
      return next;
    });
  };

  const handleCommodityChange = (uniqueId) => {
    setForm(prev => ({ ...prev, commodityUniqueId: String(uniqueId) }));
  };

  const handlePackChange = (e) => {
    const value = e.target.value;
    setForm(prev => {
      const next = { ...prev, bagType: value };
      const pt = packByStyle.get(value);
      if (pt) {
        // TWB is now an independent flag (Decision #7). A legacy pack typed 'TWB' prefills as
        // Bulk + TWB-checked (overridable); otherwise the pack's Bag/Bulk type carries through.
        if (pt.ProductType === 'TWB') { next.productType = 'Bulk'; next.isTWB = true; }
        else if (pt.ProductType) { next.productType = pt.ProductType; }
        if (next.productType === 'Bag') {
          const bc = bagCostByStyle.get(value);
          if (bc) {
            if (bc.Count != null) next.count = String(bc.Count);
            if (bc.BagWeight != null) next.bagWeight = parseFloat(bc.BagWeight).toString();
          }
          next.bulkWeight = '';
        } else {
          next.count = '';
          next.bagWeight = '';
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (busy) return;
    const cid = parseInt(form.commodityUniqueId, 10);
    if (!Number.isInteger(cid) || cid <= 0) { toast.error('Commodity is required'); return; }
    if (!form.bagType) { toast.error('Bag Type is required'); return; }
    if (form.productType === 'Bag' && (!form.count || !form.bagWeight)) {
      toast.error('Count and Bag Weight are required for Bag product type'); return;
    }
    if (form.productType !== 'Bag' && !form.bulkWeight) {
      toast.error('Bulk Weight is required for Bulk product type'); return;
    }

    setBusy(true);
    try {
      const payload = {
        commodityUniqueId: cid,
        bagType: form.bagType,
        productType: form.productType,
        isTWB:      !!form.isTWB,
        count:      form.productType === 'Bag' ? parseInt(form.count, 10) || 0 : 0,
        bagWeight:  form.productType === 'Bag' ? parseFloat(form.bagWeight) || 0 : 0,
        bulkWeight: form.productType !== 'Bag' ? parseFloat(form.bulkWeight) || 0 : 0,
        tihi:       form.tihi?.trim() || null,
      };
      await authAxios.put(`/api/chargebystyle/${item.UniqueID}`, payload);
      toast.success('Charge updated');
      setEditing(false);
      if (onUpdated) await onUpdated();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save changes';
      toast.error(msg);
      console.error('chargebystyle update', err);
    } finally {
      setBusy(false);
    }
  };

  const handleAddBoxDiff = async () => {
    const methodUniqueId = parseInt(newMethodId, 10);
    if (!Number.isInteger(methodUniqueId) || methodUniqueId <= 0) return;
    if (busy) return;
    setBusy(true);
    try {
      await authAxios.post('/api/boxdifference', {
        chargeByStyleId: item.UniqueID,
        methodUniqueId,
      });
      setNewMethodId('');
      await fetchBoxDiffs();
      toast.success('Box Difference added');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add Box Difference';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBoxDiff = async (id, methodName) => {
    if (busy) return;
    if (!window.confirm(`Delete Box Difference row "${methodName}"?`)) return;
    setBusy(true);
    try {
      await authAxios.delete(`/api/boxdifference/${id}`);
      await fetchBoxDiffs();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to delete row';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (busy) return;
    const label = `${item.Commodity ?? ''} / ${item.BagType ?? ''}`;
    if (!window.confirm(`Delete this ChargeByStyle entry (${label}) and all linked Box Difference rows? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await authAxios.delete(`/api/chargebystyle/${item.UniqueID}`);
      toast.success('Charge deleted');
      if (onDeleted) onDeleted();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to delete entry';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const selectedMethod = methods.find(m => String(m.UniqueID) === String(newMethodId));
  const titleText = `${item.Commodity ?? '—'} — ${item.BagType ?? '—'}`;

  return (
    <DrawerBackdrop onClick={onClose} role="presentation">
      <Drawer onClick={(e) => e.stopPropagation()} role="dialog" aria-label="ChargeByStyle details">
        <DrawerHeader>
          <DrawerTitle>{titleText}</DrawerTitle>
          <ActionRow>
            {!editing && (
              <>
                <Button type="button" onClick={() => setEditing(true)} disabled={busy}>Edit</Button>
                <DangerButton type="button" onClick={handleDeleteEntry} disabled={busy}>Delete</DangerButton>
              </>
            )}
            <Button type="button" onClick={onClose} style={{ background: 'var(--grey-500)' }}>Close</Button>
          </ActionRow>
        </DrawerHeader>

        <SectionCard>
          <SectionTitle>Properties</SectionTitle>
          {editing ? (
            <>
              <Field style={{ marginBottom: 10 }}>
                <FieldLabel>Commodity</FieldLabel>
                <TypeaheadSelect
                  value={form.commodityUniqueId}
                  onChange={handleCommodityChange}
                  options={commodityOptions}
                  placeholder="Select Commodity"
                  ariaLabel="Commodity"
                  size="lg"
                />
              </Field>
              <FieldGrid>
                <Field>
                  <FieldLabel>Bag Type</FieldLabel>
                  <Select name="bagType" aria-label="Pack type" value={form.bagType} onChange={handlePackChange}>
                    <option value="">Select pack…</option>
                    <optgroup label="Groups">
                      {groups.map((g) => <option key={`g-${g}`} value={g}>{g}</option>)}
                    </optgroup>
                    <optgroup label="Styles">
                      {styleNames.map((s) => <option key={`s-${s}`} value={s}>{s}</option>)}
                    </optgroup>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel>Product Type</FieldLabel>
                  <Select name="productType" value={form.productType} onChange={handleFormChange}>
                    <option value="Bag">Bag</option>
                    <option value="Bulk">Bulk</option>
                  </Select>
                </Field>
                <Field>
                  <FieldLabel>TWB</FieldLabel>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
                    <input type="checkbox" name="isTWB" checked={!!form.isTWB} onChange={handleFormChange} aria-label="TWB" />
                    <span>TWB pallet</span>
                  </label>
                </Field>
                {form.productType === 'Bag' ? (
                  <>
                    <Field>
                      <FieldLabel>Count</FieldLabel>
                      <Input type="number" name="count" min="1" step="1" value={form.count} onChange={handleFormChange} />
                    </Field>
                    <Field>
                      <FieldLabel>Bag Weight</FieldLabel>
                      <Input type="number" name="bagWeight" min="0" step="0.01" placeholder="lb" value={form.bagWeight} onChange={handleFormChange} />
                    </Field>
                  </>
                ) : (
                  <Field>
                    <FieldLabel>Bulk Weight</FieldLabel>
                    <Input type="number" name="bulkWeight" min="0" step="any" value={form.bulkWeight} onChange={handleFormChange} />
                  </Field>
                )}
                <Field>
                  <FieldLabel>Ti × Hi</FieldLabel>
                  <Input type="text" name="tihi" placeholder="0 x 0" value={form.tihi} onChange={handleFormChange} />
                </Field>
              </FieldGrid>
              <ActionRow style={{ marginTop: 12 }}>
                <Button type="button" onClick={handleSave} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
                <DangerButton type="button" onClick={() => { setEditing(false); setForm(formStateFromItem(item)); }} disabled={busy}>
                  Cancel
                </DangerButton>
              </ActionRow>
            </>
          ) : (
            <FieldGrid>
              <Field><FieldLabel>Commodity</FieldLabel><FieldValue>{item.Commodity ?? '—'}</FieldValue></Field>
              <Field><FieldLabel>Bag Type</FieldLabel><FieldValue>{item.BagType ?? '—'}</FieldValue></Field>
              <Field><FieldLabel>Product Type</FieldLabel><FieldValue>{item.ProductType ?? '—'}</FieldValue></Field>
              <Field><FieldLabel>TWB</FieldLabel><FieldValue>{(item.IsTWB === true || item.IsTWB === 1) ? 'Yes' : 'No'}</FieldValue></Field>
              {item.ProductType === 'Bag' ? (
                <>
                  <Field><FieldLabel>Count</FieldLabel><FieldValue>{item.Count ?? '—'}</FieldValue></Field>
                  <Field><FieldLabel>Bag Weight</FieldLabel><FieldValue>{fmtBagWeight(item.BagWeight) || '—'}</FieldValue></Field>
                </>
              ) : (
                <Field><FieldLabel>Bulk Weight</FieldLabel><FieldValue>{fmtBagWeight(item.BulkWeight) || '—'}</FieldValue></Field>
              )}
              <Field><FieldLabel>Ti × Hi</FieldLabel><FieldValue>{item.TiHi || '—'}</FieldValue></Field>
            </FieldGrid>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Calculated (server)</SectionTitle>
          <FieldGrid>
            <Field>
              <FieldLabel>Cost Conversion</FieldLabel>
              <FieldValue>
                {Number.isFinite(parseFloat(item.CostConversion))
                  ? parseFloat(item.CostConversion).toFixed(4)
                  : '—'}
              </FieldValue>
            </Field>
            <Field><FieldLabel>Pallets Chg</FieldLabel><FieldValue>{fmtCurrency(item.PalletsChg)}</FieldValue></Field>
            <Field><FieldLabel>Marketing Chg</FieldLabel><FieldValue>{fmtCurrency(item.MarketingChg)}</FieldValue></Field>
            <Field><FieldLabel>Packing Chg</FieldLabel><FieldValue>{fmtCurrency(item.PackingChg)}</FieldValue></Field>
            <Field><FieldLabel>Import Chg</FieldLabel><FieldValue>{fmtCurrency(item.ImportChg)}</FieldValue></Field>
            <Field><FieldLabel>Total Chg</FieldLabel><FieldValue style={{ fontWeight: 700, color: 'var(--green-800)' }}>{fmtCurrency(item.TotalChg)}</FieldValue></Field>
          </FieldGrid>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Box Difference</SectionTitle>
          {boxDiffs.length === 0 ? (
            <EmptyHint>No methods linked. Add one below.</EmptyHint>
          ) : (
            <InlineTable>
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Cost</th>
                  <th>Box Diff</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {boxDiffs.map((row) => {
                  const diff = parseFloat(row.BoxDifference);
                  const isNeg = Number.isFinite(diff) && diff < 0;
                  return (
                    <tr key={row.UniqueID}>
                      <td>{row.MethodName}</td>
                      <td>{fmtCurrency(row.Cost)}</td>
                      <td>{isNeg ? <NegativeValue>{fmtCurrency(diff)}</NegativeValue> : fmtCurrency(diff)}</td>
                      <td>
                        <DangerButton
                          type="button"
                          onClick={() => handleDeleteBoxDiff(row.UniqueID, row.MethodName)}
                          disabled={busy}
                          style={{ padding: '2px 8px', minWidth: 0 }}
                          aria-label={`Delete ${row.MethodName}`}
                        >
                          ×
                        </DangerButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </InlineTable>
          )}
          <InlineAddRow>
            <Field>
              <FieldLabel>Method</FieldLabel>
              <Select value={newMethodId} onChange={(e) => setNewMethodId(e.target.value)} aria-label="Method">
                <option value="">Select method…</option>
                {methods.map((m) => (
                  <option key={m.UniqueID} value={m.UniqueID}>
                    {m.MethodName}{m.Cost != null ? ` — ${fmtCurrency(m.Cost)}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <FieldLabel>Cost</FieldLabel>
              <Input type="text" readOnly value={selectedMethod ? fmtCurrency(selectedMethod.Cost) : ''} placeholder="(from method)" style={READONLY_STYLE} />
            </Field>
            <Button type="button" onClick={handleAddBoxDiff} disabled={busy || !newMethodId}>
              Add
            </Button>
          </InlineAddRow>
        </SectionCard>
      </Drawer>
    </DrawerBackdrop>
  );
}
